/**
 * ModulePlaceholder — placeholder screen for one REQ-043..048 admin module
 * (ODE-P01-T10).
 *
 * Renders the module title/description, a purpose-code input for purpose-bound
 * placeholder modules, and the REQ-090 state machine: the module
 * starts `pending` (data source deferred) and the user activates the guarded
 * fetch with "Muat modul"; the server guard outcome drives the terminal state.
 *
 * Traces to: ODE-PRD-REQ-043..048, ODE-PRD-REQ-090.
 */

import { useState } from 'react';
import { useAuth } from '../auth/AuthContext.js';
import type { ModuleDefinition } from '../rbac/catalog.js';
import { useDashboardModule } from '../modules/useDashboardModule.js';
import { StateView } from './StateView.js';

export function ModulePlaceholder({
  module,
}: {
  readonly module: ModuleDefinition;
}): JSX.Element {
  const { session } = useAuth();
  const [purposeCode, setPurposeCode] = useState('');
  const { state, load } = useDashboardModule(module, session?.token ?? null);

  return (
    <section aria-labelledby={`module-${module.id}`} className="module">
      <h2 id={`module-${module.id}`}>{module.title}</h2>
      <p className="module-description">{module.description}</p>
      {module.requiresPurpose && (
        <label className="purpose-field">
          Kode tujuan (x-purpose-code)
          <input
            data-testid="purpose-code"
            value={purposeCode}
            onChange={(event) => setPurposeCode(event.target.value)}
            placeholder="mis. ops.reporting"
          />
        </label>
      )}
      {state.kind === 'pending' && (
        <button type="button" data-testid="load-module" onClick={() => load(purposeCode)}>
          Muat modul
        </button>
      )}
      <StateView state={state} onRetry={() => load(purposeCode)} />
    </section>
  );
}